import React, { useState } from 'react';
import SmartImage from './SmartImage';
import { resolveImageUrl as resolveImageUrlUtil } from '../utils/urlResolver';
import ArtMuseumStoryViewer from './ArtMuseumStoryViewer';

/** Warm rotating conic rim — reads as polished amber/gold in motion. */
const STORY_RING_SPINNER_CLASS =
  'pointer-events-none absolute left-1/2 top-1/2 h-[220%] w-[220%] rounded-full bg-[conic-gradient(from_0deg,#c9a574_0%,#fde68a_12%,#fffbeb_22%,#fbbf24_38%,#f59e0b_50%,#fde68a_62%,#fff7ed_78%,#e8c896_92%,#c9a574_100%)] animate-museumInsightRingRotate opacity-[0.98]';

function StoryRing({ story, onOpen, compact = false }) {
  const cover = resolveImageUrlUtil(story.coverUrl) || story.coverUrl;
  const maxLen = compact ? 14 : 22;
  const label =
    story.title.length > maxLen ? `${story.title.slice(0, maxLen - 1)}…` : story.title;
  const avatarBox = compact ? 'h-11 w-11' : 'h-14 w-14 sm:h-16 sm:w-16';

  return (
    <button
      type="button"
      onClick={() => onOpen(story)}
      className={`group shrink-0 snap-start flex-col items-center gap-1 text-center ${
        compact ? 'flex w-[3.25rem]' : 'flex w-[4.5rem] sm:w-20'
      }`}
      title={`${story.title} — ${story.peerCount} other artists`}
    >
      <span
        className="relative inline-flex rounded-full ring-1 ring-white/90 transition duration-300 ease-out animate-museumInsightRingGlow group-hover:scale-105 group-hover:ring-amber-100/90"
      >
        <span className="absolute inset-0 overflow-hidden rounded-full" aria-hidden>
          <span className={STORY_RING_SPINNER_CLASS} />
        </span>
        <span className="relative z-[1] m-[2.5px] rounded-full bg-[#fffdf9] p-[2px] shadow-[inset_0_1px_0_rgba(255,255,255,0.92)] ring-1 ring-[#efe2d0]/90">
          <span className={`relative block overflow-hidden rounded-full bg-[#f4efe6] ${avatarBox}`}>
            {cover ? (
              <SmartImage
                src={cover}
                alt={story.title}
                className="h-full w-full object-cover"
                containerClassName="h-full w-full"
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-lg text-[#c9a574]">
                A
              </span>
            )}
            <span
              className="pointer-events-none absolute inset-0 z-[2] overflow-hidden rounded-full"
              aria-hidden
            >
              <span className="absolute -left-1/2 top-0 h-full w-[55%] bg-gradient-to-r from-transparent via-white/55 to-transparent mix-blend-screen animate-museumInsightRingSweep" />
            </span>
          </span>
        </span>
        {story.peerCount > 0 && (
          <span className="absolute -bottom-0.5 -right-0.5 z-[3] min-w-[1.1rem] rounded-full bg-[#2F251B] px-1 py-0.5 text-[9px] font-bold leading-none text-white ring-2 ring-[#fffdf9] shadow-sm">
            {story.peerCount > 9 ? '9+' : story.peerCount}
          </span>
        )}
      </span>
      <span
        className={`w-full font-medium leading-tight text-[#5A4632] group-hover:text-[#2F251B] ${
          compact ? 'line-clamp-1 text-[9px]' : 'line-clamp-2 text-[10px]'
        }`}
      >
        {label}
      </span>
    </button>
  );
}

export default function ArtMuseumInsightsStories({
  stories,
  loading,
  embedded = false,
  profileStrip = false,
}) {
  const [activeStory, setActiveStory] = useState(null);
  const compact = embedded || profileStrip;

  if (loading) {
    return (
      <div className={embedded ? 'min-w-0' : 'mb-4 rounded-[22px] border border-[#e8d4bc]/70 bg-white/50 px-3 py-4 md:px-4'}>
        {!profileStrip && (
          <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8A7357]">
            Artist Salon insights
          </p>
        )}
        <div className={`flex gap-2.5 overflow-hidden ${profileStrip ? '' : 'mt-2'}`}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex w-[3.25rem] shrink-0 flex-col items-center gap-1">
              <div
                className={`rounded-full bg-gradient-to-br from-[#fde68a] via-[#f4e0bc] to-[#e8c896] shadow-[0_0_12px_rgba(251,191,36,0.35)] animate-museumInsightRingGlow ${compact ? 'h-11 w-11' : 'h-14 w-14'}`}
              />
              <div className="h-2 w-10 animate-pulse rounded bg-[#e8d4bc]/70" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!stories?.length) return null;

  const content = (
    <>
      {profileStrip ? (
        <p className="sr-only">Artist Salon insights</p>
      ) : (
        <>
          <div className="flex items-baseline justify-between gap-2">
            <div className="min-w-0">
              <p className="whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.14em] text-[#8A7357] sm:tracking-[0.16em]">
                Artist Salon insights
              </p>
              {!compact && (
                <h3 className="mt-1 text-sm font-semibold text-[#2F251B] md:text-base">
                  Browse what others created for the same activities
                </h3>
              )}
            </div>
            {compact && !profileStrip && stories.length > 3 && (
              <span className="shrink-0 text-[10px] font-medium text-[#8A7357]">Scroll →</span>
            )}
          </div>
          {!compact && (
            <p className="mt-0.5 text-[11px] text-[#5A4632]">
              Tap a ring to open the Artist Salon viewer and explore contributions from other artists.
            </p>
          )}
        </>
      )}

      <div className={`relative ${profileStrip ? 'mt-0' : 'mt-2'}`}>
        <div
          className="flex snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-smooth pb-0.5 pt-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="list"
          aria-label="Artist Salon activity insights"
        >
          {stories.map((story) => (
            <StoryRing
              key={story.id}
              story={story}
              onOpen={setActiveStory}
              compact={compact}
            />
          ))}
        </div>
        {compact && stories.length > 3 && (
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[#fff6ec]/95 to-transparent"
            aria-hidden
          />
        )}
      </div>
    </>
  );

  return (
    <>
      {embedded ? (
        <div className="min-w-0">{content}</div>
      ) : (
        <div className="mb-4 rounded-[22px] border border-[#e8d4bc]/80 bg-gradient-to-br from-[#fffdf9]/95 to-[#fff6ec]/70 px-3 py-3 shadow-sm md:px-4 md:py-4">
          {content}
        </div>
      )}

      {activeStory && (
        <ArtMuseumStoryViewer story={activeStory} onClose={() => setActiveStory(null)} />
      )}
    </>
  );
}
